import { useEffect, useRef } from "react";
import { clsx } from "clsx";
import { useReducedMotion } from "motion/react";
import type { FeedHolder } from "@/features/subventions/components/feed/feedHolder";

type HolderSwitcherProps = {
  holders: FeedHolder[]
  activeIndex: number
  onSelect: (index: number) => void
};

export const HolderSwitcher = ({ holders, activeIndex, onSelect }: HolderSwitcherProps) => {
  const activeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();

  // Keep the selected pill visible: on a narrow viewport with several holders the active one can
  // sit off-screen in the horizontally-scrolling bar. block:"nearest" avoids any vertical jump.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
  }, [activeIndex, reduceMotion]);

  if (holders.length < 2) return null;

  return (
    <nav
      aria-label="Choisir un titulaire"
      className="flex items-center gap-1.5 overflow-x-auto rounded-full border border-gray-200 bg-gray-100 p-1.5 [scrollbar-width:none] dark:border-gray-700 dark:bg-gray-800 [&::-webkit-scrollbar]:hidden"
    >
      {holders.map((holder, index) => {
        const active = index === activeIndex;
        return (
          <button
            key={holder.id}
            ref={active ? activeRef : undefined}
            type="button"
            onClick={() => onSelect(index)}
            aria-current={active || undefined}
            className={clsx(
              "flex shrink-0 items-center gap-2 rounded-full py-1.5 pl-2 pr-3.5 transition-colors",
              active
                ? "bg-white shadow-sm dark:bg-gray-900"
                : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
            )}
          >
            <span
              className={clsx(
                "rounded-full px-2.5 py-1 text-[10.5px] font-bold whitespace-nowrap",
                active ? "bg-primary text-white" : "bg-primary/[8%] text-primary-emphasis",
              )}
            >
              {holder.role}
            </span>
            <span className="truncate text-[13.5px] font-bold text-gray-900 dark:text-white">
              {holder.displayName}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

HolderSwitcher.displayName = "HolderSwitcher";
